import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {UserService} from '../api/services/user.service';
import {PackageService} from '../api/services/package.service';
import {CreditApiService} from '../api/services/credit.service';
import {User} from '../api/interfaces/user.interface';
import {ServicePackage} from '../api/interfaces/packages.interface';
import {UserBalanceDetail} from '../api/interfaces/credit.interface';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {TrialStateService} from '../services/trial-state.service';

@Component({
  selector: 'maporium-end-trial-frame',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  styles: [`
    .end-trial-frame {
      background-color: #e7f1fa;
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 12px;
      text-align: center;
    }

    .end-trial-text {
      color: #3d85c6;
      margin: 0 0 8px 0;
      font-size: 14px;
      line-height: 1.4;
    }

    .confirm-btn {
      background-color: #3d85c6;
      color: white;
      border: none;
      padding: 0 16px;
      font-family: 'Oswald', sans-serif;
      font-size: 15px;
      text-transform: uppercase;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 29px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .confirm-btn:hover {
      background-color: #2a6ba6;
    }
  `],
  template: `
    <div *ngIf="isVisible" class="end-trial-frame">
      <p class="end-trial-text">{{ trialFrameText }}</p>
      <button *ngIf="canEndTrial" class="confirm-btn" (click)="endTrial()">{{ 'MISC_DIALOGS.END_TRIAL.END_TRIAL_BUTTON' | translate }}</button>
    </div>
  `,
})
export class EndTrialFrameComponent implements OnInit {
  /** Overrides the default credit-grant copy; the frame and button are unchanged. */
  @Input() text?: string;
  /**
   * The credits dialog only offers the frame when PROCEED can charge a card.
   * Callers that use it purely to explain a trial restriction set this false so
   * the message still shows to a trial user with no card on file.
   */
  @Input() requireCardOnFile = true;

  @Output() trialEnded = new EventEmitter<UserBalanceDetail | undefined>();

  currentUser: User | undefined;
  packages: ServicePackage[] = [];

  constructor(
    private userService: UserService,
    private packagesService: PackageService,
    private creditApiService: CreditApiService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private trialStateService: TrialStateService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.userService.getCurrentUserFromStorage();
    const userId = this.currentUser?.id;
    if (userId) {
      this.userService.getOne(userId).subscribe(user => {
        this.currentUser = user;
        this.cdr.detectChanges();
      });
    }
    this.packagesService.getAllPublicPackages().subscribe(packages => {
      this.packages = packages;
      this.cdr.detectChanges();
    });
  }

  get isOnActiveTrial(): boolean {
    const sub = this.currentUser?.subscription;
    return !!(sub?.isTrial && sub?.expirationDate && new Date(sub.expirationDate) > new Date());
  }

  // Offered only while the trial is active AND a Stripe subscription already
  // exists (card on file), so PROCEED can charge the first payment immediately.
  get canEndTrial(): boolean {
    return this.isOnActiveTrial && !!this.currentUser?.subscription?.stripeSubscriptionId;
  }

  get creditsAmount(): number {
    return this.pkg?.freeAiCredits ?? 0;
  }

  get isVisible(): boolean {
    return this.requireCardOnFile ? this.canEndTrial : this.isOnActiveTrial;
  }

  get trialFrameText(): string {
    return this.text
      ?? this.translate.instant('MISC_DIALOGS.END_TRIAL.FRAME_TEXT', {credits: this.creditsAmount.toLocaleString()});
  }

  private get pkg(): ServicePackage | undefined {
    const subPkg = this.currentUser?.subscription?.package;
    if (!subPkg) return undefined;
    return this.packages.find(p => p.id === subPkg.id) || (subPkg as ServicePackage);
  }

  private get isMonthly(): boolean {
    return this.currentUser?.subscription?.isMonthly !== false;
  }

  private eduActive(pkg: ServicePackage): boolean {
    return !!(this.currentUser?.eduDiscount && pkg.eduDiscountEnabled);
  }

  private effPrice(pkg: ServicePackage): number {
    if (this.isMonthly) {
      return this.eduActive(pkg) ? (pkg.priceMonthlyEdu ?? 0) : pkg.priceMonthly;
    }
    return this.eduActive(pkg) ? (pkg.priceYearlyEdu ?? 0) : pkg.priceYearly;
  }

  private formatPrice(price: number): string {
    if (!price) return '0';
    return price % 1 === 0 ? price.toString() : price.toFixed(2);
  }

  endTrial(): void {
    const pkg = this.pkg;
    const amount = pkg ? this.formatPrice(this.effPrice(pkg)) : '0';
    const email = this.currentUser?.email || '';
    this.dialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('MISC_DIALOGS.END_TRIAL.CONFIRM_TITLE'),
        iconClass: pkg?.icon || 'icon-smap-plan-seeker',
        message: this.translate.instant('MISC_DIALOGS.END_TRIAL.CONFIRM_MESSAGE', {plan: `${pkg?.title}`, amount, email}),
        cancelText: this.translate.instant('CREATE_MAP_DIALOG.CANCEL'),
        confirmText: this.translate.instant('USER_ACCOUNT.BUTTONS.PROCEED_UPPER'),
        onConfirm: () => {
          this.userService.endTrial().subscribe({
            next: () => {
              // Unlock every trial-gated surface at once, whichever dialog the
              // user ended the trial from: the stored user is refetched below,
              // and that race used to leave the sidebar locked until a reload.
              this.trialStateService.markTrialEnded();
              this.userService.refreshLocalUser().subscribe(() => {
                this.currentUser = this.userService.getCurrentUserFromStorage();
                this.packagesService.loadUserPackage().subscribe();
                this.cdr.detectChanges();
              });
              // Fetch the fresh balance so the smap assistant's credit counter
              // (and any caller listening via afterClosed) reflects the newly
              // granted monthly free credits.
              this.creditApiService.getBalance().subscribe({
                next: (balance) => this.trialEnded.emit(balance),
                error: () => this.trialEnded.emit(undefined),
              });
            },
            error: (err) => {
              this.dialog.open(ConfirmationDialogComponent, {
                width: '540px',
                maxWidth: '90vw',
                data: {
                  title: this.translate.instant('MISC_DIALOGS.END_TRIAL.ERROR_TITLE'),
                  iconClass: 'icon-smap-concept-warning',
                  message: this.translate.instant('MISC_DIALOGS.END_TRIAL.ERROR_MESSAGE'),
                  errorMessage: err?.error?.message || err?.message || undefined,
                  showCancel: false,
                  confirmText: this.translate.instant('USER_ACCOUNT.BUTTONS.TRY_AGAIN'),
                },
              });
            },
          });
        },
      },
    });
  }
}
